import { useCallback } from 'react';
import * as Y from 'yjs';
import { useNostr } from '@nostrify/react';
import { useQueryClient } from '@tanstack/react-query';

import { decodeContent, encodeContent } from '@/lib/doc/codec';
import {
  DOC_RELAYS,
  KIND_DOC_METADATA,
  KIND_DOC_SNAPSHOT,
  KIND_DOC_UPDATE,
  docAltTag,
  docCoordinate,
  generateDocId,
} from '@/lib/doc/kinds';
import {
  buildDocMetadataEvent,
  canPubkeyEdit,
  parseDocMetadata,
} from '@/lib/doc/metadata';
import { deleteCachedDoc, saveCachedMetadata, saveDocState } from '@/lib/doc/storage';
import type { DocMetadata, DocSummary } from '@/lib/doc/types';
import { useCurrentUser } from '@/hooks/useCurrentUser';

type DocRelayGroup = ReturnType<ReturnType<typeof useNostr>['nostr']['group']>;

/**
 * Document-level operations used by the dashboard (create, rename,
 * duplicate, delete). All events are signed with the user's Nostr
 * identity and published to the document relay group.
 */
export function useDocActions() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['nostr', 'doc-list'] });
  }, [queryClient]);

  const createDocument = useCallback(async (): Promise<DocSummary | undefined> => {
    if (!user) return undefined;
    const docNostr = nostr.group([...DOC_RELAYS]);
    const docId = generateDocId();
    const createdAt = Math.floor(Date.now() / 1000);

    const metadataEvent = await user.signer.signEvent(
      buildDocMetadataEvent({
        docId,
        title: 'Untitled document',
        collaborators: [],
        createdAt,
      }),
    );
    await docNostr.event(metadataEvent, { signal: AbortSignal.timeout(7000) });

    const metadata = parseDocMetadata(metadataEvent);
    if (metadata) {
      await saveCachedMetadata(metadata);
    }

    // Publish an initial (empty) snapshot so that other clients can open
    // the document instantly without waiting for updates.
    try {
      const ydoc = new Y.Doc();
      const content = await encodeContent(Y.encodeStateAsUpdate(ydoc));
      ydoc.destroy();
      const snapshotEvent = await user.signer.signEvent({
        kind: KIND_DOC_SNAPSHOT,
        content,
        tags: [
          ['d', docId],
          ['a', docCoordinate(user.pubkey, docId)],
          docAltTag('Untitled document'),
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      await docNostr.event(snapshotEvent, { signal: AbortSignal.timeout(7000) });
    } catch (error) {
      console.warn('[nostr-office] initial snapshot publish failed', error);
    }

    invalidate();
    return {
      ownerPubkey: user.pubkey,
      docId,
      title: 'Untitled document',
      updatedAt: metadataEvent.created_at,
      createdAt,
      deleted: false,
      collaboratorPubkeys: [],
      role: 'owner',
    };
  }, [user, nostr, invalidate]);

  /** Fetch the latest metadata for a document from the doc relays. */
  const fetchMetadata = useCallback(async (
    docNostr: DocRelayGroup,
    summary: Pick<DocSummary, 'ownerPubkey' | 'docId'>,
  ): Promise<DocMetadata | undefined> => {
    const [event] = await docNostr.query(
      [{ kinds: [KIND_DOC_METADATA], authors: [summary.ownerPubkey], '#d': [summary.docId], limit: 1 }],
      { signal: AbortSignal.timeout(5000) },
    );
    return event ? parseDocMetadata(event) : undefined;
  }, []);

  const renameDocument = useCallback(async (summary: DocSummary, title: string): Promise<boolean> => {
    if (!user || user.pubkey !== summary.ownerPubkey) return false;
    try {
      const docNostr = nostr.group([...DOC_RELAYS]);
      const metadata = await fetchMetadata(docNostr, summary);
      const event = await user.signer.signEvent(
        buildDocMetadataEvent({
          docId: summary.docId,
          title,
          collaborators: metadata?.collaborators ?? [],
          createdAt: metadata?.createdAt ?? summary.createdAt,
          deleted: metadata?.deleted,
        }),
      );
      await docNostr.event(event, { signal: AbortSignal.timeout(6000) });
      const published = parseDocMetadata(event);
      if (published) await saveCachedMetadata(published);
      invalidate();
      return true;
    } catch (error) {
      console.warn('[nostr-office] rename failed', error);
      return false;
    }
  }, [user, nostr, fetchMetadata, invalidate]);

  /** Fetch the merged Yjs state of a document from the doc relays. */
  const fetchDocState = useCallback(async (
    docNostr: DocRelayGroup,
    summary: Pick<DocSummary, 'ownerPubkey' | 'docId'>,
    metadata: DocMetadata | undefined,
  ): Promise<Uint8Array> => {
    const coord = docCoordinate(summary.ownerPubkey, summary.docId);
    const authorized = (pubkey: string) =>
      metadata ? canPubkeyEdit(pubkey, metadata) : pubkey === summary.ownerPubkey;

    const snapshots = await docNostr.query(
      [{ kinds: [KIND_DOC_SNAPSHOT], '#d': [summary.docId], limit: 10 }],
      { signal: AbortSignal.timeout(6000) },
    );
    let snapshotAt = 0;
    let snapshotBytes: Uint8Array | undefined;
    for (const event of snapshots) {
      if (!authorized(event.pubkey)) continue;
      if (event.created_at <= snapshotAt) continue;
      const bytes = await decodeContent(event.content);
      if (bytes) {
        snapshotAt = event.created_at;
        snapshotBytes = bytes;
      }
    }

    const ydoc = new Y.Doc();
    if (snapshotBytes) {
      try {
        Y.applyUpdate(ydoc, snapshotBytes);
      } catch {
        // ignore malformed payload
      }
    }

    const updates = await docNostr.query(
      [{ kinds: [KIND_DOC_UPDATE], '#a': [coord], since: snapshotAt, limit: 400 }],
      { signal: AbortSignal.timeout(6000) },
    );
    for (const event of updates) {
      if (!authorized(event.pubkey)) continue;
      const bytes = await decodeContent(event.content);
      if (!bytes) continue;
      try {
        Y.applyUpdate(ydoc, bytes);
      } catch {
        // ignore malformed payload
      }
    }

    const state = Y.encodeStateAsUpdate(ydoc);
    ydoc.destroy();
    return state;
  }, []);

  const duplicateDocument = useCallback(async (summary: DocSummary): Promise<DocSummary | undefined> => {
    if (!user) return undefined;
    try {
      const docNostr = nostr.group([...DOC_RELAYS]);
      const metadata = await fetchMetadata(docNostr, summary);
      const state = await fetchDocState(docNostr, summary, metadata);
      const docId = generateDocId();
      const createdAt = Math.floor(Date.now() / 1000);
      const title = `${summary.title} (copy)`.slice(0, 200);

      const metadataEvent = await user.signer.signEvent(
        buildDocMetadataEvent({ docId, title, collaborators: [], createdAt }),
      );
      await docNostr.event(metadataEvent, { signal: AbortSignal.timeout(7000) });

      const content = await encodeContent(state);
      const snapshotEvent = await user.signer.signEvent({
        kind: KIND_DOC_SNAPSHOT,
        content,
        tags: [
          ['d', docId],
          ['a', docCoordinate(user.pubkey, docId)],
          docAltTag(title),
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      await docNostr.event(snapshotEvent, { signal: AbortSignal.timeout(8000) });

      // Save locally so the copy opens instantly.
      await saveDocState(user.pubkey, docId, state);
      const published = parseDocMetadata(metadataEvent);
      if (published) await saveCachedMetadata(published);

      invalidate();
      return {
        ownerPubkey: user.pubkey,
        docId,
        title,
        updatedAt: Math.floor(Date.now() / 1000),
        createdAt,
        deleted: false,
        collaboratorPubkeys: [],
        role: 'owner',
      };
    } catch (error) {
      console.warn('[nostr-office] duplicate failed', error);
      return undefined;
    }
  }, [user, nostr, fetchMetadata, fetchDocState, invalidate]);

  const deleteDocument = useCallback(async (summary: DocSummary): Promise<boolean> => {
    if (!user || user.pubkey !== summary.ownerPubkey) return false;
    try {
      const docNostr = nostr.group([...DOC_RELAYS]);
      const metadata = await fetchMetadata(docNostr, summary);
      const event = await user.signer.signEvent(
        buildDocMetadataEvent({
          docId: summary.docId,
          title: metadata?.title ?? summary.title,
          collaborators: metadata?.collaborators ?? [],
          createdAt: metadata?.createdAt ?? summary.createdAt,
          deleted: true,
        }),
      );
      await docNostr.event(event, { signal: AbortSignal.timeout(6000) });

      // Best-effort NIP-09 deletion request for the addressable events.
      try {
        const deletion = await user.signer.signEvent({
          kind: 5,
          content: 'Document deleted',
          tags: [['a', docCoordinate(summary.ownerPubkey, summary.docId)]],
          created_at: Math.floor(Date.now() / 1000),
        });
        await docNostr.event(deletion, { signal: AbortSignal.timeout(4000) });
      } catch {
        // ignore
      }

      await deleteCachedDoc(summary.ownerPubkey, summary.docId);
      invalidate();
      return true;
    } catch (error) {
      console.warn('[nostr-office] delete failed', error);
      return false;
    }
  }, [user, nostr, fetchMetadata, invalidate]);

  return { createDocument, renameDocument, duplicateDocument, deleteDocument };
}
